import React, { useContext, useState } from 'react';
import {
  ICountry,
  IHistoryAllocationCountry,
  useCountryNamesQuery,
  useCreateAllocationCountryMutation,
  useUpdateAllocationCountryMutation
} from '../../../../generated/graphql';
import DialogBox from '../../../Common/Layout/DialogBox';
import { Box } from '@mui/material';
import EditButton from '../../../Common/Buttons/EditButton';
import { Colors } from '../../../../utils/theme';
import CancelButton from '../../../Common/Buttons/CancelButton';
import SaveButton from '../../../Common/Buttons/SaveButton';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import { canUserEdit } from '../../../../utils/rightUtils';
import AutocompleteComponent from '../../../Common/Fields/AutocompleteComponent';
import InputComponent from '../../../Common/Fields/InputComponent';

interface IProps {
  stockId: string;
  handleClose: (saved: boolean, isNew?: boolean, countryInput?: IHistoryAllocationCountry) => void;
  countryInput: IHistoryAllocationCountry | null;
}

type IErrors = {
  country: string | null;
  percent: string | null;
  year: string | null;
};

const defaultErrorsObj: IErrors = {
  country: null,
  percent: null,
  year: null
};

const errorTexts: IErrors = {
  country: 'Country is required!',
  percent: 'Country percent is required!',
  year: 'Year is required!'
};

const CountryForm: React.FunctionComponent<IProps> = ({
  stockId,
  handleClose,
  countryInput
}: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [editable, setEditable] = useState(countryInput === null);
  const [country, setCountry] = useState(countryInput?.country || null);
  const [percent, setPercent] = useState<number | null>(countryInput?.value || null);
  const [year, setYear] = useState<number | null>(countryInput?.year || null);
  const canEdit = canUserEdit(state.user, Types.Stock);
  const countries = useCountryNamesQuery();
  const [createAllocationCountry, createAllocationCountryResult] =
    useCreateAllocationCountryMutation();
  const [updateAllocationCountry, updateAllocationCountryResult] =
    useUpdateAllocationCountryMutation();

  const handleCountryChange = (_: unknown, newValue: React.SetStateAction<ICountry | null>) => {
    setFormErrors({ ...formErrors, country: newValue === null ? errorTexts.country : null });
    setCountry(newValue);
  };

  const handlePercentChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.currentTarget.value === '' ? null : Number(event.currentTarget.value);
    setFormErrors({ ...formErrors, percent: value === null ? errorTexts.percent : null });
    setPercent(value);
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.currentTarget.value === '' ? null : Number(event.currentTarget.value);
    setFormErrors({ ...formErrors, year: value === null ? errorTexts.year : null });
    setYear(value);
  };

  const handleCancel = () => {
    if (editable && countryInput !== null) {
      setCountry(countryInput?.country);
      setPercent(countryInput?.value);
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = { ...defaultErrorsObj };
    if (country === null) {
      hasErrors = true;
      errors.country = errorTexts.country;
    }
    if (percent === null) {
      hasErrors = true;
      errors.percent = errorTexts.percent;
    }
    if (percent === null) {
      hasErrors = true;
      errors.year = errorTexts.year;
    }

    if (hasErrors) {
      setFormErrors(errors);
    } else {
      handleSave(countryInput === null ? 'create' : 'update');
    }
  };

  const handleSave = (type: 'create' | 'update') => {
    const input = {
      country_id: country?.id as string,
      value: percent as number,
      year: year as number
    };
    if (type === 'create') {
      createAllocationCountry({
        variables: {
          stock_id: stockId,
          input
        },
        update(_, { data }) {
          handleClose(true, true, data?.result as IHistoryAllocationCountry);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Allocation country created'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    } else {
      updateAllocationCountry({
        variables: {
          id: countryInput?.id as string,
          input
        },
        update(_, { data }) {
          handleClose(true, false, data?.result as IHistoryAllocationCountry);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Allocation country updated'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    }
  };

  return (
    <DialogBox
      id="allocation-country-dialog"
      width={410}
      title={countryInput ? 'Allocation Country Details' : 'Create Allocation Country'}
      onClose={handleClose}
      childrenSx={{ px: '40px', py: '20px' }}
    >
      {countryInput && (
        <Box sx={{ textAlign: 'right', width: '100%' }}>
          <EditButton
            id="allocation-country-form-edit-button"
            onClick={() => setEditable(true)}
            disabled={editable || !canEdit}
            editButtonColor={editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          mt: '20px',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '24px'
        }}
      >
        <AutocompleteComponent
          id="countries-autocomplete"
          data-testid="countries-autocomplete"
          options={(countries.data?.result as never[]) || []}
          getOptionLabel={(option: ICountry) => option?.name}
          label="Country"
          values={country}
          onChange={handleCountryChange}
          disabled={
            countries.loading ||
            !editable ||
            createAllocationCountryResult.loading ||
            updateAllocationCountryResult.loading
          }
          errorMsg={formErrors.country}
          additionalStyle={{ gridColumn: 'span 2' }}
        />
        <InputComponent
          id="percent-value"
          data-testid="percent-value"
          label="Country%"
          type="number"
          value={percent}
          onChange={handlePercentChange}
          disabled={
            countries.loading ||
            !editable ||
            createAllocationCountryResult.loading ||
            updateAllocationCountryResult.loading
          }
          errorMsg={formErrors.percent}
        />
        <InputComponent
          id="year"
          data-testid="year"
          label="Year"
          type="number"
          value={year}
          onChange={handleYearChange}
          disabled={
            countries.loading ||
            !editable ||
            createAllocationCountryResult.loading ||
            updateAllocationCountryResult.loading
          }
          errorMsg={formErrors.year}
        />
      </Box>
      <Box
        className="centralize"
        sx={{
          gridColumn: 'span 3',
          marginTop: '50px',
          paddingBottom: '45px'
        }}
      >
        <CancelButton
          id="cancel-allocation-country-button"
          onClick={handleCancel}
          disabled={createAllocationCountryResult.loading || updateAllocationCountryResult.loading}
          text={countryInput === null || editable ? 'Cancel' : 'Close'}
        />
        <SaveButton
          id="save-allocation-country-button"
          onClick={handleSubmit}
          disabled={
            countries.loading ||
            !editable ||
            createAllocationCountryResult.loading ||
            updateAllocationCountryResult.loading
          }
          text={countryInput === null ? 'Create' : 'Save'}
        />
      </Box>
    </DialogBox>
  );
};

export default CountryForm;
